import React, { useState } from 'react';
import { BarChart2, ChevronDown, ChevronUp, Code, AlertCircle, Search } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент для анализа и объяснения редиректов
 */
const RedirectAnalyzer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [redirectCode, setRedirectCode] = useState('');
  const [explanation, setExplanation] = useState(null);
  const [example, setExample] = useState(null);

  // Обработчик анализа кода редиректа
  const analyzeRedirect = () => {
    const code = parseInt(redirectCode);
    
    if (isNaN(code) || code < 100 || code > 599) {
      setExplanation({
        error: true,
        title: 'Некорректный код',
        description: 'Введите действительный HTTP-код статуса (от 100 до 599).'
      });
      setExample(null);
      return;
    }
    
    // Объяснения для различных кодов редиректов
    if (code === 301) {
      setExplanation({
        error: false,
        title: '301 Moved Permanently (Перемещено навсегда)',
        description: 'Этот код указывает, что URL-адрес ресурса был изменен навсегда. Поисковые системы обновляют свои индексы, заменяя старый URL на новый. Этот редирект передаёт до 90-99% "ссылочного веса" SEO.',
        seoImpact: 'Положительное при правильном использовании. Рекомендуется для постоянных изменений структуры сайта, переезда на новый домен или протокол (HTTP → HTTPS).',
        browserBehavior: 'Браузеры кэшируют этот редирект, и при повторном запросе исходного URL могут напрямую переходить на новый URL без обращения к серверу.'
      });
      setExample({
        title: 'Пример настройки 301-редиректа',
        codeExamples: [
          {
            lang: 'Apache (.htaccess)',
            code: 'Redirect 301 /old-page.html https://example.com/new-page.html'
          },
          {
            lang: 'Nginx',
            code: 'rewrite ^/old-page.html$ https://example.com/new-page.html permanent;'
          },
          {
            lang: 'PHP',
            code: "<?php\nheader('HTTP/1.1 301 Moved Permanently');\nheader('Location: https://example.com/new-page.html');\nexit();\n?>"
          }
        ]
      });
    } else if (code === 302) {
      setExplanation({
        error: false,
        title: '302 Found (Найдено)',
        description: 'Этот код указывает на временное перенаправление. Изначально он был определён как "Moved Temporarily" (Перемещено временно). Поисковые системы сохраняют исходный URL в индексе.',
        seoImpact: 'Не рекомендуется для постоянных изменений, так как передаёт минимальное количество "ссылочного веса". При длительном использовании поисковики могут со временем интерпретировать его как 301.',
        browserBehavior: 'Браузеры не кэшируют этот редирект и каждый раз запрашивают исходный URL.'
      });
      setExample({
        title: 'Пример настройки 302-редиректа',
        codeExamples: [
          {
            lang: 'Apache (.htaccess)',
            code: 'Redirect 302 /temp-page.html https://example.com/actual-page.html'
          },
          {
            lang: 'Nginx',
            code: 'rewrite ^/temp-page.html$ https://example.com/actual-page.html redirect;'
          },
          {
            lang: 'PHP',
            code: "<?php\nheader('HTTP/1.1 302 Found');\nheader('Location: https://example.com/actual-page.html');\nexit();\n?>"
          }
        ]
      });
    } else if (code === 303) {
      setExplanation({
        error: false,
        title: '303 See Other (Смотреть другое)',
        description: 'Этот код используется для перенаправления после отправки формы, указывая клиенту использовать GET-запрос для следующего ресурса. Позволяет избежать повторной отправки формы при обновлении страницы.',
        seoImpact: 'Практически не влияет на SEO, так как обычно используется в контексте взаимодействия пользователя с формами и не затрагивает индексацию.',
        browserBehavior: 'Браузер меняет метод запроса на GET независимо от того, какой метод использовался изначально.'
      });
      setExample({
        title: 'Пример использования 303-редиректа',
        codeExamples: [
          {
            lang: 'PHP',
            code: "<?php\n// После обработки POST-запроса формы\nheader('HTTP/1.1 303 See Other');\nheader('Location: https://example.com/thank-you.html');\nexit();\n?>"
          }
        ]
      });
    } else if (code === 307) {
      setExplanation({
        error: false,
        title: '307 Temporary Redirect (Временное перенаправление)',
        description: 'Строгая версия 302 редиректа. Означает, что ресурс временно доступен по другому URL. В отличие от 302, гарантирует сохранение метода HTTP-запроса (GET, POST и т.д.) при перенаправлении.',
        seoImpact: 'Аналогично 302: не рекомендуется для постоянных изменений. Сохраняет индексацию исходного URL.',
        browserBehavior: 'Браузер обязан использовать тот же метод (GET, POST, PUT и т.д.), что и в исходном запросе, для доступа к перенаправленному ресурсу.'
      });
      setExample({
        title: 'Пример настройки 307-редиректа',
        codeExamples: [
          {
            lang: 'Apache (.htaccess)',
            code: 'RewriteEngine On\nRewriteRule ^old-api/(.*)$ /new-api/$1 [R=307,L]'
          },
          {
            lang: 'PHP',
            code: "<?php\nheader('HTTP/1.1 307 Temporary Redirect');\nheader('Location: https://example.com/temporary-location');\nexit();\n?>"
          }
        ]
      });
    } else if (code === 308) {
      setExplanation({
        error: false,
        title: '308 Permanent Redirect (Постоянное перенаправление)',
        description: 'Строгая версия 301 редиректа. Указывает, что ресурс был перемещен навсегда, но в отличие от 301, гарантирует сохранение метода HTTP-запроса при перенаправлении.',
        seoImpact: 'Аналогично 301: очень хорошо для SEO при постоянных изменениях. Передаёт большую часть "ссылочного веса" на новый URL.',
        browserBehavior: 'Браузеры кэшируют этот редирект, но в отличие от 301, сохраняют оригинальный метод запроса при перенаправлении.'
      });
      setExample({
        title: 'Пример настройки 308-редиректа',
        codeExamples: [
          {
            lang: 'Apache (.htaccess)',
            code: 'RewriteEngine On\nRewriteRule ^api/v1/(.*)$ /api/v2/$1 [R=308,L]'
          },
          {
            lang: 'Nginx',
            code: 'rewrite ^/api/v1/(.*)$ /api/v2/$1 permanent;'
          },
          {
            lang: 'PHP',
            code: "<?php\nheader('HTTP/1.1 308 Permanent Redirect');\nheader('Location: https://example.com/new-api-endpoint');\nexit();\n?>"
          }
        ]
      });
    } else {
      // Для других HTTP-кодов
      setExplanation({
        error: code >= 300 && code < 400 ? false : true,
        title: `Код ${code} ${getStatusTextByCode(code)}`,
        description: getDescriptionByCode(code)
      });
      setExample(null);
    }
  };

  // Вспомогательная функция для получения текстового описания кода
  const getStatusTextByCode = (code) => {
    const statuses = {
      300: 'Multiple Choices (Множественный выбор)',
      304: 'Not Modified (Не изменялось)',
      305: 'Use Proxy (Использовать прокси)',
      306: 'Switch Proxy (Переключение прокси)', // устаревший
      400: 'Bad Request (Некорректный запрос)',
      401: 'Unauthorized (Не авторизован)',
      403: 'Forbidden (Запрещено)',
      404: 'Not Found (Не найдено)',
      500: 'Internal Server Error (Внутренняя ошибка сервера)',
      502: 'Bad Gateway (Плохой шлюз)',
      503: 'Service Unavailable (Сервис недоступен)',
      504: 'Gateway Timeout (Таймаут шлюза)'
    };
    
    return statuses[code] || 'Неизвестный статус';
  };

  // Вспомогательная функция для получения описания кода
  const getDescriptionByCode = (code) => {
    if (code >= 300 && code < 400 && ![301, 302, 303, 307, 308].includes(code)) {
      return 'Это код перенаправления, но он редко используется на практике. Рекомендуется использовать более стандартные коды редиректов, такие как 301, 302, 307 или 308.';
    } else if (code >= 400 && code < 500) {
      return 'Это код ошибки клиента, а не код перенаправления. Он указывает на проблему с запросом пользователя.';
    } else if (code >= 500) {
      return 'Это код ошибки сервера, а не код перенаправления. Он указывает на внутреннюю проблему на сервере.';
    } else {
      return 'Это не код перенаправления. Коды редиректов находятся в диапазоне 300-399.';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mb-8">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center w-full justify-between text-lg font-medium text-gray-900 dark:text-white mb-2"
      >
        <div className="flex items-center">
          <BarChart2 className="w-5 h-5 mr-2 text-indigo-500" />
          Анализатор редиректов
        </div>
        <div className="text-indigo-500">
          {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </div>
      </button>
      
      {isOpen && (
        <div className="mt-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Введите HTTP-код редиректа, чтобы получить его описание, влияние на SEO и примеры настройки.
          </p>
          
          <div className="flex mb-4">
            <input
              type="text"
              className="form-input w-24 mr-3"
              placeholder="301"
              value={redirectCode}
              onChange={(e) => setRedirectCode(e.target.value)}
            />
            <Button
              onClick={analyzeRedirect}
              icon={<Search className="w-4 h-4" />}
              primary
            >
              Проанализировать
            </Button>
          </div>
          
          {explanation && (
            <div className={`rounded-lg p-4 ${explanation.error 
              ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' 
              : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
            }`}>
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-0.5">
                  {explanation.error ? (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  ) : (
                    <Code className="w-5 h-5 text-blue-500" />
                  )}
                </div>
                <div className="ml-3">
                  <h3 className={`text-base font-medium ${explanation.error 
                    ? 'text-red-800 dark:text-red-300' 
                    : 'text-blue-800 dark:text-blue-300'
                  }`}>
                    {explanation.title}
                  </h3>
                  <p className={`mt-2 text-sm ${explanation.error 
                    ? 'text-red-700 dark:text-red-400' 
                    : 'text-blue-700 dark:text-blue-400'
                  }`}>
                    {explanation.description}
                  </p>
                  
                  {explanation.seoImpact && (
                    <div className="mt-3">
                      <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                        Влияние на SEO
                      </h4>
                      <p className="text-sm text-blue-700 dark:text-blue-400">
                        {explanation.seoImpact}
                      </p>
                    </div>
                  )}
                  
                  {explanation.browserBehavior && (
                    <div className="mt-3">
                      <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                        Поведение браузера
                      </h4>
                      <p className="text-sm text-blue-700 dark:text-blue-400">
                        {explanation.browserBehavior}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {example && (
            <div className="mt-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <h4 className="text-base font-medium text-gray-900 dark:text-white mb-3">
                {example.title}
              </h4>
              
              <div className="space-y-3">
                {example.codeExamples.map((code, index) => (
                  <div key={index}>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {code.lang}
                    </div>
                    <pre className="bg-gray-100 dark:bg-gray-800 rounded-md p-3 text-xs overflow-x-auto">
                      <code className="text-gray-800 dark:text-gray-300 whitespace-pre">
                        {code.code}
                      </code>
                    </pre>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="mt-4 p-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg text-sm text-indigo-700 dark:text-indigo-300">
            <p><strong>Совет:</strong> Для постоянных изменений URL используйте 301-редирект. Для временных перенаправлений — 302.</p>
            <p className="mt-1"><strong>Популярные в России редиректы:</strong> 301 (постоянный), 302 (временный), 307 (временный с сохранением метода).</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RedirectAnalyzer;